import React, { useState } from "react";
import { Heart, Plus, Trash2, Check, Loader2 } from "lucide-react";
import { useFavorites } from "../../hooks/useFavorites";
import { usePartyStore } from "../../stores/partyStore";
import { useAuthStore } from "../../stores/authStore";
import { addSongToQueue } from "../../lib/firebase";
import type { FavoriteSong, YouTubeSearchResult } from "../../types";

export const FavoritesList: React.FC = () => {
  const { favorites, loading, removeFavorite } = useFavorites();
  const { currentParty, setActiveTab } = usePartyStore();
  const { user } = useAuthStore();

  const [addingId, setAddingId] = useState<string | null>(null);
  const [addedSuccessId, setAddedSuccessId] = useState<string | null>(null);

  const handleAddFromFavorites = async (song: FavoriteSong) => {
    if (!currentParty || !user) return;
    setAddingId(song.videoId);

    try {
      const searchItem: YouTubeSearchResult = {
        id: { kind: "youtube#video", videoId: song.videoId },
        snippet: {
          title: song.title,
          channelTitle: song.channelTitle,
          thumbnails: {
            default: { url: song.thumbnailUrl },
          },
        },
        status: {
          embeddable: song.embeddable ?? true,
        },
      };

      await addSongToQueue(currentParty.id, searchItem, user);

      setAddedSuccessId(song.videoId);
      setTimeout(() => {
        setAddedSuccessId(null);
        setActiveTab("queue");
      }, 800);
    } catch (err: any) {
      alert(`Failed to add song to queue: ${err.message}`);
    } finally {
      setAddingId(null);
    }
  };

  if (loading) {
    return (
      <div className="py-12 text-center space-y-3">
        <Loader2 className="w-8 h-8 mx-auto text-accent animate-spin" />
        <p className="text-xs text-text-muted">Loading your favorites playlist...</p>
      </div>
    );
  }

  if (favorites.length === 0) {
    return (
      <div className="bg-card/50 border border-dashed border-border rounded-3xl p-10 text-center space-y-4">
        <div className="w-16 h-16 mx-auto rounded-3xl bg-red-500/10 flex items-center justify-center text-red-400">
          <Heart className="w-8 h-8" />
        </div>
        <div className="space-y-1">
          <h3 className="text-xl font-bold font-heading text-text-main">No Favorite Songs Yet</h3>
          <p className="text-xs text-text-muted max-w-xs mx-auto">
            Search for songs in the <strong>Add a Song</strong> tab and tap the heart icon to save your go-to karaoke anthems!
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between px-1">
        <span className="text-xs font-semibold text-text-muted flex items-center gap-1.5">
          <Heart className="w-3.5 h-3.5 text-red-500 fill-red-500" />
          Your Personal Go-To Karaoke Playlist
        </span>
        <span className="text-xs font-mono font-bold text-text-main">
          {favorites.length} {favorites.length === 1 ? "track" : "tracks"}
        </span>
      </div>

      <div className="grid grid-cols-1 gap-2.5">
        {favorites.map((song) => {
          const isAdding = addingId === song.videoId;
          const isAdded = addedSuccessId === song.videoId;

          return (
            <div
              key={song.videoId}
              className="bg-card p-3 sm:p-4 rounded-2xl border border-border hover:border-accent/40 transition-all flex items-center gap-3 sm:gap-4 shadow-md"
            >
              {/* Thumbnail */}
              <div className="relative w-16 h-12 sm:w-20 sm:h-14 rounded-xl overflow-hidden bg-background shrink-0 shadow-sm">
                <img
                  src={song.thumbnailUrl}
                  alt={song.title}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>

              {/* Details */}
              <div className="min-w-0 flex-1 space-y-0.5">
                <h4 className="text-xs sm:text-sm font-bold text-text-main truncate">
                  {song.title}
                </h4>
                <p className="text-[11px] text-text-muted truncate">
                  {song.channelTitle}
                </p>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => handleAddFromFavorites(song)}
                  disabled={isAdding || isAdded}
                  className={`py-2 px-3 sm:px-4 rounded-xl font-bold text-xs flex items-center gap-1.5 transition-all shadow-md cursor-pointer ${
                    isAdded
                      ? "bg-green-600 text-white"
                      : "bg-primary hover:bg-primary-hover text-white disabled:opacity-50"
                  }`}
                  title="Add to Party Queue"
                >
                  {isAdding ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : isAdded ? (
                    <>
                      <Check className="w-4 h-4" />
                      <span className="hidden sm:inline">Added!</span>
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4" />
                      <span className="hidden sm:inline">Add to Queue</span>
                    </>
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => removeFavorite(song.videoId)}
                  className="p-2 rounded-xl text-text-muted hover:text-red-400 hover:bg-red-500/10 transition-colors cursor-pointer"
                  title="Remove from favorites"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
